import { toLonLat } from 'ol/proj'
import { create } from 'xmlbuilder2'
import JSZip from 'jszip'
import { RouteBearing, RoutePoint } from './use-lawnmower'
import fs from 'fs/promises'
import { Polygon } from 'ol/geom'
import { XMLBuilder } from 'xmlbuilder2/lib/interfaces'
import { Coordinate } from 'ol/coordinate'
import { DOMAIN, DRONE_TYPE, PAYLOAD_TYPE } from '/@/types/device'
import { generatePitchYawWaylines } from './use-kml-pitch-yaw-oblique'
import { FlightParams } from '../types/flight-params'
import dayjs from 'dayjs'
/**
 * Converts OpenLayers Polygon (possibly with holes) to DJI WPML Polygon structure
 * @param {import('ol/geom/Polygon').default} polygon - OpenLayers Polygon
 * @returns {Object} - xmlbuilder2 fragment for <Polygon>
 */
function addDJIPolygon (builder:XMLBuilder, polygon:Polygon):XMLBuilder {
  // Get all rings: index 0 = outer, index 1+ = inner holes
  const rings = polygon.getCoordinates()

  // If projection is not WGS84, transform first
  const transformedRings = rings.map(ring => ring.map(coord => toLonLat(coord)))
  const ringsToUse = transformedRings
  const polygonBuilder = builder.ele('Polygon')

  // === Outer Boundary (required - only one) ===
  if (ringsToUse.length > 0) {
    const outerRing = ringsToUse[0]
    const outerCoords = formatRingToDJI(outerRing)

    polygonBuilder
      .ele('outerBoundaryIs')
      .ele('LinearRing')
      .ele('coordinates')
      .txt(outerCoords)
      .up()
      .up()
      .up()
  }

  // === Inner Boundaries (holes) - multiple allowed ===
  for (let i = 1; i < ringsToUse.length; i++) {
    const innerRing = ringsToUse[i]
    const innerCoords = formatRingToDJI(innerRing)

    polygonBuilder
      .ele('innerBoundaryIs')
      .ele('LinearRing')
      .ele('coordinates')
      .txt('\n')
      .txt(innerCoords)
      .up()
      .up()
      .up()
  }

  return polygonBuilder
}

/**
 * Helper: Convert ring coordinates to "lon,lat,0 lon,lat,0 ..." + close the ring
 */
function formatRingToDJI (ring:Coordinate[]):string {
  const coords = ring.map(([lon, lat]) => {
    const cleanLon = Number(lon.toFixed(12))
    const cleanLat = Number(lat.toFixed(12))
    return `${cleanLon},${cleanLat},0`
  })

  // Close the ring (first point == last point)
  // if (coords.length > 0) {
  //   coords.push(coords[0])
  // }

  const coordsString = '\n                ' + coords.join('\n                ') + '\n              '
  return coordsString
}

export function normalizeOlPoints (
  points: number[][],
  sourceCrs: 'EPSG:3857' | 'EPSG:4326'
): [number, number][] {
  return points.map(p =>
    sourceCrs === 'EPSG:3857'
      ? (toLonLat(p) as [number, number])
      : (p as [number, number])
  )
}

export function generateWaylinesWpml (
  routeBearing: RouteBearing
): string {
  const flightParams = routeBearing.flightParams
  const executeHeightMode = flightParams.surfaceFollowModeEnable === true ? 'WGS84' : 'relativeToStartPoint'
  const route = routeBearing.routePointBearings
  const triggerType = flightParams.triggerDistanceFlag === true ? 'multipleDistance' : 'multipleTiming'
  const triggerVal = flightParams.triggerDistanceFlag === true ? routeBearing.footprint.height : (routeBearing.footprint.height / flightParams.waypointSpeed)

  const routeLastIndex = route.length - 1

  let root = create({ version: '1.0', encoding: 'UTF-8' })
    .ele('kml', {
      xmlns: 'http://www.opengis.net/kml/2.2',
      'xmlns:wpml': 'http://www.dji.com/wpmz/1.0.6'
    })
    .ele('Document')

  // Mission config
  root = generateMissonConfig(root, flightParams)

  // Wayline
  const folder = root.ele('Folder')
  folder.ele('wpml:templateId').txt('0').up()

  // executeHeightMode:
  // 1. WGS84: Ellipsoid height model.
  // 2. relativeToStartPoint: Relative take-off point altitude model.
  // 3. realTimeFollowSurface: Using real-time follow surface mode. Only supported by M3E/M3T/M3M.
  folder.ele('wpml:executeHeightMode').txt(executeHeightMode).up()
  folder.ele('wpml:waylineId').txt('0').up()
  // Maximum flight speed of the current drone
  folder.ele('wpml:autoFlightSpeed').txt(String(flightParams.flightSpeed)).up()

  // === ACTION GROUP ===
  folder.ele('wpml:startActionGroup')
    .ele('wpml:action')
    .ele('wpml:actionId').txt('0').up()
    .ele('wpml:actionActuatorFunc').txt('gimbalRotate').up()
    .ele('wpml:actionActuatorFuncParam')
    .ele('wpml:gimbalHeadingYawBase').txt('aircraft').up()
    .ele('wpml:gimbalRotateMode').txt('absoluteAngle').up()
    .ele('wpml:gimbalPitchRotateEnable').txt('1').up()
    .ele('wpml:gimbalPitchRotateAngle').txt(String(route[0].cameraPitch)).up()
    .ele('wpml:gimbalRollRotateEnable').txt('0').up()
    .ele('wpml:gimbalRollRotateAngle').txt(String(route[0].cameraRoll)).up()
    .ele('wpml:gimbalYawRotateEnable').txt('1').up()
    .ele('wpml:gimbalYawRotateAngle').txt(String(route[0].cameraYaw)).up()
    .ele('wpml:gimbalRotateTimeEnable').txt('0').up()
    .ele('wpml:gimbalRotateTime').txt('10').up()
    .ele('wpml:payloadPositionIndex').txt('0').up()
    .up()
    .up()
    .ele('wpml:action')
    .ele('wpml:actionId').txt('1').up()
    .ele('wpml:actionActuatorFunc').txt('hover').up()
    .ele('wpml:actionActuatorFuncParam')
    .ele('wpml:hoverTime').txt(String(0.5)).up()
    .up()
    .up()
    .ele('wpml:action')
    .ele('wpml:actionId').txt('2').up()
    .ele('wpml:actionActuatorFunc').txt('setFocusType').up()
    .ele('wpml:actionActuatorFuncParam')
    .ele('wpml:cameraFocusType').txt('manual').up()
    .ele('wpml:payloadPositionIndex').txt('0').up()
    .up()
    .up()
    .ele('wpml:action')
    .ele('wpml:actionId').txt('3').up()
    .ele('wpml:actionActuatorFunc').txt('focus').up()
    .ele('wpml:actionActuatorFuncParam')
    .ele('wpml:focusX').txt('0').up()
    .ele('wpml:focusY').txt('0').up()
    .ele('wpml:focusRegionWidth').txt('0').up()
    .ele('wpml:focusRegionHeight').txt('0').up()
    .ele('wpml:isPointFocus').txt('0').up()
    .ele('wpml:isInfiniteFocus').txt('1').up()
    .ele('wpml:payloadPositionIndex').txt('0').up()
    .up()
    .up()
    .ele('wpml:action')
    .ele('wpml:actionId').txt('4').up()
    .ele('wpml:actionActuatorFunc').txt('hover').up()
    .ele('wpml:actionActuatorFuncParam')
    .ele('wpml:hoverTime').txt(String(1)).up()
    .up()
    .up()
    .up()
  // ==================== ACTION GROUP

  // Waypoints
  // === PLACEMARK ===
  route.forEach((r:RoutePoint, index:number) => {
    const lon = r.lonlat[0]
    const lat = r.lonlat[1]

    let waypointTurnMode = 'coordinateTurn'
    let waypointHeadingAngleEnable = '1'
    if (r.index === 0 || r.index === routeLastIndex) {
      waypointTurnMode = 'toPointAndStopWithDiscontinuityCurvature'
      if (r.index === routeLastIndex) waypointHeadingAngleEnable = '0'
    }

    const placemark = folder.ele('Placemark')
      .ele('Point')
      .ele('coordinates').txt(`\n            ${lon},${lat}\n          `).up()
      .up()
      .ele('wpml:index').txt(String(r.index)).up()
      // waypointSpeed:  Required if "wpml:useGlobalSpeed" is 0.
      .ele('wpml:waypointSpeed').txt(String(flightParams.waypointSpeed)).up()
      .ele('wpml:executeHeight').txt(String(r.altitude)).up()
      // .ele('wpml:altitudeMode').txt('aboveGroundLevel').up()
      // .ele('wpml:isRealtimeSurfaceFollow').txt('1').up()
      // .ele('wpml:surfaceRelativeHeight').txt(String(TERRAIN_ALTITUDE)).up()

      // === WAYPOINT HEADING PARAM start
      .ele('wpml:waypointHeadingParam')
      // waypointHeadingMode:
      // 1. followWayline:Along course direction. The nose of the aircraft follows the course direction to the next waypoint.
      // 2. manually: The user can manually control the nose orientation of the aircraft during the flight to the next waypoint.
      // 3. fixed: The nose of the aircraft maintains the yaw angle of the aircraft to the next waypoint after the waypoint action has been performed.
      // 4. smoothTransition: Customized. The target yaw angle for a waypoint is given by "wpml:waypointHeadingAngle" and transitions evenly to the target yaw angle of the next waypoint during the flight segment.
      // 5. towardPOI: The aircraft heading faces the point of interest.
      .ele('wpml:waypointHeadingMode').txt('followWayline').up()
      .ele('wpml:waypointHeadingAngle').txt(String(r.bearing)).up()

    // waypointPoiPoint: Required if "wpml:waypointHeadingMode" is "towardPOI".
    // .ele('wpml:waypointPoiPoint').txt('0.000000,0.000000,0.000000').up()
    // .ele('wpml:waypointHeadingPoiIndex').txt('0').up()
      .ele('wpml:waypointHeadingAngleEnable').txt(waypointHeadingAngleEnable).up()
      .ele('wpml:waypointHeadingPathMode').txt('followBadArc').up()
      .up()
      // ==================== WAYPOINT HEADING PARAM

      // === WAYPOINT TURN PARAM start ===
      .ele('wpml:waypointTurnParam')
      // waypointTurnMode:
    // 1. coordinateTurn: Coordinated turns, no dips, early turns.
    // 2. toPointAndStopWithDiscontinuityCurvature:
    //    Fly in a straight line and the aircraft stops at the point.
    // 3. toPointAndStopWithContinuityCurvature:
    //    Fly in a curve and the aircraft stops at the point.
    // 4. toPointAndPassWithContinuityCurvature:
    //    Fly in a curve and the aircraft will not stop at the point.
    // Note: The setting method of "Turns before waypoint. Flies through." mode in DJI Pilot2/FlightHub 2 is
    // 1) Set the "wpml:waypointTurnMode" to "toPointAndPassWithContinuityCurvature".
    // 2) Set the "wpml:useStraightLine" to 1.
      .ele('wpml:waypointTurnMode').txt(waypointTurnMode)
      .up()
    // waypointTurnDampingDist
    // (0, the maximum length of wayline segment]
    // * Note:
    //  The wayline segment between two waypoints should be greater than the sum of the turn intercepts of two waypoints.
    //  This element defines how far to the waypoint that the aircraft should turn.
      .ele('wpml:waypointTurnDampingDist').txt(String(r.turnDamping)).up()
      .up()
      // === WAYPOINT TURN PARAM end ===

      // useStraightLine:
      // Note:
      // Required if "waypointTurnMode" in "wpml:waypointTurnParam" is set to "toPointAndStopWithContinuityCurvature"
      // or
      // "toPointAndPassWithContinuityCurvature".
      // If this element is set, local definitions override global definitions.
      .ele('wpml:useStraightLine').txt('1').up()

    if (r.index === 0) {
      // === ACTION GROUP ===
      placemark.ele('wpml:actionGroup')
        .ele('wpml:actionGroupId').txt('0').up()
        .ele('wpml:actionGroupStartIndex').txt('0').up()
        .ele('wpml:actionGroupEndIndex').txt(String(routeLastIndex)).up()
        .ele('wpml:actionGroupMode').txt('sequence').up()
        // === ACTION GROUP ACTION TRIGGER ===
        .ele('wpml:actionTrigger')
        .ele('wpml:actionTriggerType').txt('betweenAdjacentPoints').up()
        .up()
        // =========================== ACTION GROUP ACTION TRIGGER
        // === ACTION GROUP ACTION ===
        .ele('wpml:action')
        .ele('wpml:actionId').txt('0').up()
        .ele('wpml:actionActuatorFunc').txt('gimbalAngleLock').up()
        .up()
        // =========================== ACTION GROUP ACTION
        .up()
        // =========================== ACTION GROUP
        // === ACTION GROUP ===
        .ele('wpml:actionGroup')
        .ele('wpml:actionGroupId').txt('1').up()
        .ele('wpml:actionGroupStartIndex').txt('0').up()
        .ele('wpml:actionGroupEndIndex').txt(String(routeLastIndex)).up()
        .ele('wpml:actionGroupMode').txt('sequence').up()
        // === ACTION GROUP ACTION TRIGGER ===
        .ele('wpml:actionTrigger')
        // reachPoint: Executed on arrival at the waypoint.
        // betweenAdjacentPoints: Flight routine segment trigger. Rotate the gimbal evenly.
        // multipleTiming: Same time trigger.
        // multipleDistance: Same distance trigger.
        .ele('wpml:actionTriggerType').txt(triggerType).up()
        // "actionTriggerType" is "multipleTiming" => the interval time in s.
        // if "multipleDistance" => the interval distance in m.
        .ele('wpml:actionTriggerParam').txt(String(triggerVal)).up()
        .up()
        // =========================== ACTION GROUP ACTION TRIGGER
        // === ACTION GROUP ACTION ===
        .ele('wpml:action')
        .ele('wpml:actionId').txt('0').up()
        .ele('wpml:actionActuatorFunc').txt('gimbalRotate').up()
      // === ACTION GROUP ACTION actionActuatorFuncParam ===
        .ele('wpml:actionActuatorFuncParam')
        .ele('wpml:gimbalHeadingYawBase').txt('aircraft').up()
        .ele('wpml:gimbalRotateMode').txt('absoluteAngle').up()
        .ele('wpml:gimbalPitchRotateEnable').txt('1').up()
        .ele('wpml:gimbalPitchRotateAngle').txt(String(r.cameraPitch)).up()
        .ele('wpml:gimbalRollRotateEnable').txt('0').up()
        .ele('wpml:gimbalRollRotateAngle').txt(String(r.cameraRoll)).up()
        .ele('wpml:gimbalYawRotateEnable').txt('1').up()
        .ele('wpml:gimbalYawRotateAngle').txt(String(r.cameraYaw)).up()
        .ele('wpml:gimbalRotateTimeEnable').txt('0').up()
        .ele('wpml:gimbalRotateTime').txt('10').up()
        .ele('wpml:payloadPositionIndex').txt('0').up()
        .up()
        // =========================== ACTION GROUP ACTION actionActuatorFuncParam
        .up()
        // =========================== ACTION GROUP ACTION
        .ele('wpml:action')
        .ele('wpml:actionId').txt('1').up()
        .ele('wpml:actionActuatorFunc').txt('startContinuousShooting').up()
        .ele('wpml:actionActuatorFuncParam')
        .ele('wpml:payloadPositionIndex').txt('0').up()
        .ele('wpml:useGlobalPayloadLensIndex').txt('0').up()
        .ele('wpml:payloadLensIndex').txt('visable').up()
        .up()
        .up()
        // =========================== ACTION GROUP
    }

    if (r.index === routeLastIndex) {
      placemark.ele('wpml:actionGroup')
        .ele('wpml:actionGroupId').txt('2').up()
        .ele('wpml:actionGroupStartIndex').txt(String(routeLastIndex)).up()
        .ele('wpml:actionGroupEndIndex').txt(String(routeLastIndex)).up()
        .ele('wpml:actionGroupMode').txt('sequence').up()
        .ele('wpml:actionTrigger')
        .ele('wpml:actionTriggerType').txt('reachPoint').up()
        .up()
        .ele('wpml:action')
        .ele('wpml:actionId').txt('0').up()
        .ele('wpml:actionActuatorFunc').txt('stopContinuousShooting').up()
        .ele('wpml:actionActuatorFuncParam')
        .ele('wpml:payloadPositionIndex').txt('0').up()
        .ele('wpml:payloadLensIndex').txt('visable').up()
        .up()
        .up()
        .ele('wpml:action')
        .ele('wpml:actionId').txt('1').up()
        .ele('wpml:actionActuatorFunc').txt('gimbalAngleUnlock').up()
        .up()
        .up()
    }
    placemark.ele('wpml:waypointGimbalHeadingParam')
      .ele('wpml:waypointGimbalPitchAngle').txt('0').up()
      .ele('wpml:waypointGimbalYawAngle').txt('0').up()
      .up()
      .ele('wpml:isRisky').txt('0').up()
      .ele('wpml:waypointWorkType').txt('0').up()
      .up()
  })
  folder.up()
  // ================= PLACEMARK

  return root.end({ prettyPrint: true })
}

function generateMissonConfig (builder: XMLBuilder, flightParams: FlightParams) {
  const takeoffAltitude = flightParams.takeoffAltitude
  const flightSpeed = flightParams.flightSpeed
  const droneName:string = flightParams.droneName

  // === mission config start ===
  builder.ele('wpml:missionConfig')
    .ele('wpml:flyToWaylineMode').txt('safely').up()
    .ele('wpml:finishAction').txt('goHome').up()
    .ele('wpml:exitOnRCLost').txt('executeLostAction').up()
    .ele('wpml:executeRCLostAction').txt('goBack').up()
    .ele('wpml:takeOffSecurityHeight').txt(String(takeoffAltitude)).up()
    .ele('wpml:globalTransitionalSpeed').txt(String(flightSpeed)).up()
    .ele('wpml:droneInfo')
    .ele('wpml:droneEnumValue').txt(String(DRONE_TYPE[droneName as keyof typeof DRONE_TYPE])).up()
    .ele('wpml:droneSubEnumValue').txt(DOMAIN.DRONE).up()
    .up()
    .ele('wpml:payloadInfo')
    .ele('wpml:payloadEnumValue').txt(String(PAYLOAD_TYPE[droneName as keyof typeof PAYLOAD_TYPE])).up()
    .ele('wpml:payloadSubEnumValue').txt('0').up()
    .ele('wpml:payloadPositionIndex').txt('0').up()
    .up()
    .up()
    // === mission config stop ===
  return builder
}

export function generateWaypointTemplateKml (route: RouteBearing): string {
  const flightParams = route.flightParams
  const frontOverlap = flightParams.frontOverlap
  const sideOverlap = flightParams.sideOverlap
  const polygon = route.boundaryPolygon
  const shootType = flightParams.triggerDistanceFlag ? 'distance' : 'time'
  let heightModeString = 'relativeToStartPoint'
  let followEnable = 0
  const now = Date.now()

  if (flightParams.surfaceFollowModeEnable === true) {
    heightModeString = 'aboveGroundLevel'
    followEnable = 1
  }

  let doc = create({ version: '1.0', encoding: 'UTF-8' })
    .ele('kml', {
      xmlns: 'http://www.opengis.net/kml/2.2',
      'xmlns:wpml': 'http://www.dji.com/wpmz/1.0.2'
    })
    .ele('Document')
    .ele('wpml:author').txt('HanwhaDrone').up()
    .ele('wpml:createTime').txt(String(now)).up()
    .ele('wpml:updateTime').txt(String(now)).up()

  doc = generateMissonConfig(doc, flightParams)
    .ele('Folder')
    .ele('wpml:templateType').txt('mapping2d').up()
    .ele('wpml:templateId').txt('0').up()
    .ele('wpml:waylineCoordinateSysParam')
    .ele('wpml:coordinateMode').txt('WGS84').up()
    .ele('wpml:heightMode').txt(heightModeString).up() // aboveGroundLevel; relativeToStartPoint
    .ele('wpml:globalShootHeight').txt(String(route.globalShootHeight)).up()

  if (followEnable) {
    doc = doc.ele('wpml:surfaceFollowModeEnable').txt(String(followEnable)).up()
      .ele('wpml:isRealtimeSurfaceFollow').txt(String(followEnable)).up() // only M3E/M3T/M3M
      .ele('wpml:surfaceRelativeHeight').txt(String(route.surfaceRelativeHeight)).up()
    // .ele('wpml:altitudeMode').txt('aboveGroundLevel').up()
  }

  doc = doc.up()
    .ele('wpml:autoFlightSpeed').txt(flightParams.flightSpeed).up()

  // Placemark tag
    .ele('Placemark')
    .ele('wpml:caliFlightEnable').txt('0').up()
    .ele('wpml:elevationOptimizeEnable').txt('0').up()
    .ele('wpml:smartObliqueEnable').txt(flightParams.obliqueFlightFlag === true ? '1' : '0').up()
    .ele('wpml:facadeWaylineEnable').txt('0').up()
    .ele('wpml:isLookAtSceneSet').txt('0').up()
    .ele('wpml:smartObliqueGimbalPitch').txt(String(-90 + flightParams.obliqueFlightAngle.pitch)).up()
    .ele('wpml:shootType').txt(shootType).up()
    .ele('wpml:direction').txt(String(route.flightDirectionDeg)).up()
    .ele('wpml:margin').txt('0').up()
    .ele('wpml:efficiencyFlightModeEnable').txt('0').up()

  // === overlap start ====
    .ele('wpml:overlap')
    /// TODO: check this overlap to defined angle from input
    .ele('wpml:orthoLidarOverlapH').txt(String(frontOverlap)).up()
    .ele('wpml:orthoLidarOverlapW').txt(String(sideOverlap)).up()
    .ele('wpml:orthoCameraOverlapH').txt(String(frontOverlap)).up()
    .ele('wpml:orthoCameraOverlapW').txt(String(sideOverlap)).up()
    .up()
  // === overlap end ====

  doc = addDJIPolygon(doc, polygon)
    .up()
    .ele('wpml:ellipsoidHeight').txt(String(flightParams.takeoffAltitude)).up()
    .ele('wpml:height').txt(String(flightParams.takeoffAltitude)).up()
    .up()
  // === placemark end ====
  // === payloadparam start ===
    .ele('wpml:payloadParam')
    .ele('wpml:payloadPositionIndex').txt('0').up()
    .ele('wpml:dewarpingEnable').txt('0').up()
    .ele('wpml:returnMode').txt('singleReturnFirst').up()
    .ele('wpml:samplingRate').txt('240000').up()
    .ele('wpml:scanningMode').txt('nonRepetitive').up()
    .ele('wpml:modelColoringEnable').txt('0').up()
    .ele('wpml:imageFormat').txt('visable').up()
    .up()
  // === payloadparam end ===
    .up()
    .up()

  const xmlString = doc.end({ prettyPrint: true, indent: '  ' })
  return xmlString
}

export async function createDock2Kmz (
  waylinesWpml: string,
  templateKml: string
): Promise<Blob> {
  const zip = new JSZip()
  const wpmz = zip.folder('wpmz')!

  wpmz.file('waylines.wpml', waylinesWpml)
  wpmz.file('template.kml', templateKml)

  return zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE'
  })
}

async function downloadZip (zipBlob:Blob, customZipName?: string): Promise<void> {
  // Auto filename for XML
  const now = new Date()
  const dateStr = now.toISOString()
    .slice(0, 19)
    .replace('-', '')
    .replace(/T/, '')
    .replace(/:/g, '')

  // 3. Auto filename for ZIP
  const zipFilename = `${customZipName}.kmz` || `flight-${dateStr}.kmz`

  // 4. Trigger download
  const url = URL.createObjectURL(zipBlob)
  const link = document.createElement('a')
  link.href = url
  link.download = zipFilename
  document.body.appendChild(link)
  link.click()

  // Cleanup
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  console.log(`✅ Download started: ${zipFilename}`)
}

export async function buildMissionKmzBlob (
  route: RouteBearing,
  // sourceCrs: "EPSG:3857" | "EPSG:4326"
) {
  const template = generateWaypointTemplateKml(route)
  const points = route.routePointBearings

  const flightParams:FlightParams = route.flightParams
  const droneName = flightParams.droneName

  let waylines:string
  if (flightParams.obliqueFlightFlag) {
    waylines = generatePitchYawWaylines(
      points,
      {
        height: flightParams.surfaceFollowModeEnable === true ? flightParams.takeoffAltitude : flightParams.flightAltitude,
        speed: flightParams.waypointSpeed,
        obliqueAngle: flightParams.obliqueFlightAngle, // ← just change this number
        executeHeightMode: flightParams.surfaceFollowModeEnable === true ? 'WGS84' : 'relativeToStartPoint',
        droneEnumValue: DRONE_TYPE[droneName as keyof typeof DRONE_TYPE],
        droneSubEnumValue: 0,
        payloadEnumValue: PAYLOAD_TYPE[droneName as keyof typeof PAYLOAD_TYPE],
        payloadSubEnumValue: 0,
        globalTransitionalSpeed: flightParams.flightSpeed,
        takeOffSecurityHeight: flightParams.takeoffAltitude,
      }
    )
  } else {
    waylines = generateWaylinesWpml(route)
  }
  const blob = await createDock2Kmz(waylines, template)
  return blob
}

export async function buildDock2MissionKmz (
  route: RouteBearing,
  // sourceCrs: "EPSG:3857" | "EPSG:4326"
) {
  // const lonLatPoints = normalizeOlPoints(olPoints, sourceCrs);
  // const lonLatPoints = route
  const kmzBlob = await buildMissionKmzBlob(route)

  const flightParams:FlightParams = route.flightParams

  const zipName =
    '' + flightParams.flightAltitude + (flightParams.surfaceFollowModeEnable === true ? 's' : 'a') +
    (flightParams.obliqueFlightFlag === true ? 'o' : 'f') +
    (flightParams.waypointSpeed < 10 ? '0' : '') + flightParams.waypointSpeed +
    flightParams.frontOverlap +
    flightParams.sideOverlap

  downloadZip(kmzBlob, zipName)
}

const sanitizeFileName = (name:string) => {
  return name
    .replace(/[<>:"/|?*._\\]/g, '') // replace invalid chars
    .replace(/\s+/g, ' ') // optional: normalize spaces
    .trim() || 'default'
}

export function generateSafeKmzFileName (fileName: string) {
  const ext = fileName.includes('.')
    ? '.' + fileName.split('.').pop()
    : '.kmz'

  const baseName = fileName.replace(/\.[^/.]+$/, '')
  const safeName = sanitizeFileName(baseName) + ext
  return safeName
}
